import { useState, Fragment, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { SearchIcon, EyeIcon, EyeOffIcon } from "@heroicons/react/solid";
import {
  PlusCircleIcon,
  KeyIcon,
  ClipboardCopyIcon,
} from "@heroicons/react/outline";
import { Dialog } from "@headlessui/react";
import ActionDropdown from "../../components/ActionDropdown";
import fire from "./../../utils/Toastr";
import ThirdPartyServices from "../../services/ThirdParty";
import { HollowDotsSpinner } from "react-epic-spinners";
import CreateApplicationModal from "./CreateApplicationModal";
import Paginate from "../../utils/Paginate";

export default function ThirdPartyApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeysModal, setShowKeysModal] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [generatedKeys, setGeneratedKeys] = useState(null);

  // Pagination states
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageLimit: 10,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // State for showing secret keys in table
  const [showSecretKeys, setShowSecretKeys] = useState({});

  // Fetch applications with pagination
  useEffect(() => {
    fetchApplications(pagination.pageIndex);
  }, [pagination.pageIndex]);

  const fetchApplications = async (page = 1) => {
    setLoading(true);
    try {
      const response = await ThirdPartyServices.listApplications(
        page,
        pagination.pageLimit,
      );

      if (response.status && response.data) {
        // Handle the new response structure with pagination
        if (response.data.result && Array.isArray(response.data.result)) {
          setApplications(response.data.result);
          setPagination((prev) => ({
            ...prev,
            pageIndex: response.data.pageIndex || page,
            totalPages: response.data.totalPages || 1,
            totalCount: response.data.totalCount || 0,
            hasNextPage: response.data.hasNextPage || false,
            hasPreviousPage: response.data.hasPreviousPage || false,
          }));
        } else if (Array.isArray(response.data)) {
          // Fallback for non-paginated response
          setApplications(response.data);
          setPagination((prev) => ({
            ...prev,
            pageIndex: 1,
            totalPages: 1,
            totalCount: response.data.length,
            hasNextPage: false,
            hasPreviousPage: false,
          }));
        } else {
          setError("Unexpected response structure");
        }
      } else {
        setError("Failed to fetch applications");
      }
    } catch (err) {
      setError("Failed to fetch applications");
      fire("error", "Failed to fetch applications");
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  // Search for specific application
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fire("warning", "Please enter search parameter");
      return;
    }

    setLoading(true);
    try {
      // Try with query parameter first
      const response = await ThirdPartyServices.getApplication(searchTerm);

      if (response.status && response.data) {
        // If searching returns a single application, display it
        if (response.data.applicationDetails) {
          setApplications([response.data.applicationDetails]);
          setPagination((prev) => ({
            ...prev,
            pageIndex: 1,
            totalPages: 1,
            totalCount: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          }));
          fire("success", "Application found");
        } else if (
          response.data.result &&
          Array.isArray(response.data.result)
        ) {
          // If response has paginated structure
          setApplications(response.data.result);
          setPagination((prev) => ({
            ...prev,
            pageIndex: response.data.pageIndex || 1,
            totalPages: response.data.totalPages || 1,
            totalCount: response.data.totalCount || response.data.result.length,
            hasNextPage: response.data.hasNextPage || false,
            hasPreviousPage: response.data.hasPreviousPage || false,
          }));
          fire(
            "success",
            `${response.data.result.length} application(s) found`,
          );
        }
      } else {
        setApplications([]);
        setPagination((prev) => ({
          ...prev,
          pageIndex: 1,
          totalPages: 0,
          totalCount: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }));
        fire("info", "No application found with the given parameters");
      }
    } catch (err) {
      console.error("Search error details:", err);
      setError("Error occurred while searching for application");
      fire("error", "Error occurred while searching for application");
    } finally {
      setLoading(false);
    }
  };

  // Handle application creation success
  const handleApplicationCreated = (keys) => {
    setGeneratedKeys(keys);
    setShowCreateModal(false);
    setShowKeysModal(true);
    fetchApplications(pagination.pageIndex);
  };

  // Copy to clipboard
  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    fire("success", `${fieldName} copied to clipboard!`);
  };

  // Toggle secret key visibility for a specific app
  const toggleSecretKeyVisibility = (appId) => {
    setShowSecretKeys((prev) => ({
      ...prev,
      [appId]: !prev[appId],
    }));
  };

  // Reset search and pagination
  const resetSearch = () => {
    setSearchTerm("");
    setShowSecretKeys({});
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
    fetchApplications(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, pageIndex: page }));
  };

  // Handle Enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Layout current={"thirdparty"} pageTag="thirdparty">
      <Fragment>
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Header with search and create button */}
              <div className="sm:flex sm:items-center mb-6">
                <div className="sm:flex-auto flex-1">
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Third-Party Applications
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Manage API applications and their credentials
                  </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    type="button"
                    className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto"
                  >
                    <PlusCircleIcon className="w-4 mr-1.5" />
                    Create Application
                  </button>
                </div>
              </div>

              {/* Search Section */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  {/* Search Input */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search Application (by ID or Name)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10 focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Enter application ID or name"
                      />
                    </div>
                  </div>

                  {/* Search Button */}
                  <div className="flex">
                    <button
                      onClick={handleSearch}
                      className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 w-full"
                    >
                      Search
                    </button>
                  </div>

                  {/* Reset Button */}
                  <div className="flex">
                    <button
                      onClick={resetSearch}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* Applications Table */}
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <HollowDotsSpinner color="#4A90E2" />
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-700">{error}</p>
                  <button
                    onClick={resetSearch}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Try again
                  </button>
                </div>
              ) : applications.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
                  <KeyIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No applications found
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {searchTerm ? "Try a different search or " : ""}
                    Create your first third-party application to get started.
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
                    >
                      <PlusCircleIcon className="w-4 mr-1.5" />
                      Create Application
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[200px]">
                            Application
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                            Contact Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px]">
                            API Key
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[250px]">
                            Secret Key
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] sticky right-0 bg-gray-50 z-10">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {applications.map((app, i) => (
                          <tr
                            key={app.applicationId}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10 min-w-[200px]">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {app.applicationName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {app.applicationId}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[180px]">
                              {app.contactEmailAddress}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[250px]">
                              <div className="flex items-center">
                                <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-[220px]">
                                  {app.apiKey}
                                </code>
                                <button
                                  onClick={() =>
                                    copyToClipboard(app.apiKey, "API Key")
                                  }
                                  className="ml-2 text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 shrink-0"
                                  title="Copy API Key"
                                >
                                  <ClipboardCopyIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[250px]">
                              <div className="flex items-center">
                                <div className="relative">
                                  <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-[220px]">
                                    {showSecretKeys[app.applicationId]
                                      ? app.secretKey
                                      : "•".repeat(32)}
                                  </code>
                                </div>
                                <div className="ml-2 flex space-x-1 shrink-0">
                                  <button
                                    onClick={() =>
                                      toggleSecretKeyVisibility(
                                        app.applicationId,
                                      )
                                    }
                                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                                    title={
                                      showSecretKeys[app.applicationId]
                                        ? "Hide Secret Key"
                                        : "Show Secret Key"
                                    }
                                  >
                                    {showSecretKeys[app.applicationId] ? (
                                      <EyeOffIcon className="w-4 h-4" />
                                    ) : (
                                      <EyeIcon className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() =>
                                      copyToClipboard(
                                        app.secretKey,
                                        "Secret Key",
                                      )
                                    }
                                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                                    title="Copy Secret Key"
                                  >
                                    <ClipboardCopyIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white z-10 min-w-[120px]">
                              <ActionDropdown
                                dropdownWidth="w-56"
                                items={[
                                  { label: "View All Details", onClick: () => { setSelectedApp(app); setShowKeysModal(true); } },
                                  { label: "Copy API Key", onClick: () => copyToClipboard(app.apiKey, "API Key") },
                                  { label: "Copy Secret Key", onClick: () => copyToClipboard(app.secretKey, "Secret Key") },
                                  { label: showSecretKeys[app.applicationId] ? "Hide Secret Key" : "Show Secret Key", onClick: () => toggleSecretKeyVisibility(app.applicationId) },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Component - Only show if not searching */}
                  {!searchTerm && pagination.totalPages > 1 && (
                    <Paginate
                      pageIndex={pagination.pageIndex}
                      totalCount={pagination.totalCount}
                      totalPages={pagination.totalPages}
                      hasPreviousPage={pagination.hasPreviousPage}
                      hasNextPage={pagination.hasNextPage}
                      setPageIndex={handlePageChange}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </main>

        {/* Create Application Modal */}
        <CreateApplicationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleApplicationCreated}
        />

        {/* View Keys Modal */}
        <Dialog
          open={showKeysModal}
          onClose={() => {
            setShowKeysModal(false);
            setShowSecretKey(false);
            setSelectedApp(null);
            setGeneratedKeys(null);
          }}
          className="relative z-50"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="mx-auto max-w-2xl rounded-lg bg-white p-6">
              <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                {generatedKeys
                  ? "Application Created Successfully!"
                  : "Application Details"}
              </Dialog.Title>

              <div className="space-y-6">
                {generatedKeys ? (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <KeyIcon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Important Security Notice
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              Store these credentials securely. They will not be
                              shown again.
                            </p>
                            <p className="mt-1">
                              ⚠️ Keep the secret key confidential! Do not share
                              or commit to version control.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          API Key
                        </label>
                        <div className="flex items-center">
                          <code className="flex-1 text-sm bg-gray-100 p-3 rounded font-mono break-all">
                            {generatedKeys.apiKey}
                          </code>
                          <button
                            onClick={() =>
                              copyToClipboard(generatedKeys.apiKey, "API Key")
                            }
                            className="ml-2 text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                            title="Copy API Key"
                          >
                            <ClipboardCopyIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Secret Key
                        </label>
                        <div className="flex items-center">
                          <div className="flex-1 relative">
                            <code className="text-sm bg-gray-100 p-3 rounded font-mono block break-all">
                              {showSecretKey
                                ? generatedKeys.secretKey
                                : "•".repeat(48)}
                            </code>
                          </div>
                          <div className="ml-2 flex space-x-1">
                            <button
                              onClick={() => setShowSecretKey(!showSecretKey)}
                              className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                              title={
                                showSecretKey
                                  ? "Hide Secret Key"
                                  : "Show Secret Key"
                              }
                            >
                              {showSecretKey ? (
                                <EyeOffIcon className="w-5 h-5" />
                              ) : (
                                <EyeIcon className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  generatedKeys.secretKey,
                                  "Secret Key",
                                )
                              }
                              className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                              title="Copy Secret Key"
                            >
                              <ClipboardCopyIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  selectedApp && (
                    <>
                      <div className="bg-gray-50 rounded-md p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">
                          Application Information
                        </h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">
                                Application Name
                              </p>
                              <p className="text-sm font-medium">
                                {selectedApp.applicationName}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                Application ID
                              </p>
                              <p className="text-sm font-mono">
                                {selectedApp.applicationId}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                Contact Email
                              </p>
                              <p className="text-sm">
                                {selectedApp.contactEmailAddress}
                              </p>
                            </div>
                          </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            API Key
                          </label>
                          <div className="flex items-center">
                            <code className="flex-1 text-sm bg-gray-100 p-3 rounded font-mono break-all">
                              {selectedApp.apiKey}
                            </code>
                            <button
                              onClick={() =>
                                copyToClipboard(selectedApp.apiKey, "API Key")
                              }
                              className="ml-2 text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                            >
                              <ClipboardCopyIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Secret Key
                          </label>
                          <div className="flex items-center">
                            <div className="flex-1 relative">
                              <code className="text-sm bg-gray-100 p-3 rounded font-mono block break-all">
                                {showSecretKey
                                  ? selectedApp.secretKey
                                  : "•".repeat(48)}
                              </code>
                            </div>
                            <div className="ml-2 flex space-x-1">
                              <button
                                onClick={() => setShowSecretKey(!showSecretKey)}
                                className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                                title={
                                  showSecretKey
                                    ? "Hide Secret Key"
                                    : "Show Secret Key"
                                }
                              >
                                {showSecretKey ? (
                                  <EyeOffIcon className="w-5 h-5" />
                                ) : (
                                  <EyeIcon className="w-5 h-5" />
                                )}
                              </button>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedApp.secretKey,
                                    "Secret Key",
                                  )
                                }
                                className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100"
                                title="Copy Secret Key"
                              >
                                <ClipboardCopyIcon className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowKeysModal(false);
                    setShowSecretKey(false);
                    setSelectedApp(null);
                    setGeneratedKeys(null);
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Close
                </button>
                {selectedApp && (
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `${selectedApp.apiKey}\n${selectedApp.secretKey}`,
                        "All Credentials",
                      )
                    }
                    className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Copy All Keys
                  </button>
                )}
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Fragment>
    </Layout>
  );
}
